import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSiteSettings } from '../../contexts/SiteSettingsContext';

const LANGUAGES = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'ar', label: 'العربية', flag: '🇸🇦' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const { settings } = useSiteSettings();
  const { t, i18n } = useTranslation();

  const currentLang = LANGUAGES.find((l) => l.code === i18n.language) ?? LANGUAGES[0];

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isHome = location.pathname === '/';
  const isTransparent = isHome && !scrolled;

  const NAV_LINKS = [
    { path: '/', labelKey: 'nav.home' },
    { path: '/services', labelKey: 'nav.services' },
    { path: '/track', labelKey: 'nav.track' },
    { path: '/about', labelKey: 'nav.about' },
    { path: '/contact', labelKey: 'nav.contact' },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isTransparent
          ? 'bg-transparent'
          : 'bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 h-18 flex items-center justify-between" style={{ height: '72px' }}>
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2.5">
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center"
            style={{ background: 'var(--primary)' }}
          >
            <i className="ri-flight-takeoff-line text-white text-lg" />
          </div>
          <span
            className={`font-heading font-bold text-xl tracking-tight transition-colors ${
              isTransparent ? 'text-white' : 'text-gray-900'
            }`}
          >
            {settings.companyName}
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-8">
          {NAV_LINKS.map(({ path, labelKey }) => {
            const active = location.pathname === path;
            return (
              <Link
                key={path}
                to={path}
                className={`text-sm font-medium transition-colors whitespace-nowrap ${
                  isTransparent
                    ? active
                      ? 'text-white'
                      : 'text-white/75 hover:text-white'
                    : active
                    ? 'text-gray-900'
                    : 'text-gray-500 hover:text-gray-900'
                }`}
                style={active && !isTransparent ? { color: 'var(--primary)' } : {}}
              >
                {t(labelKey)}
              </Link>
            );
          })}
        </nav>

        {/* CTA + Language */}
        <div className="hidden md:flex items-center gap-3">
          {/* Language switcher */}
          <div className="relative" ref={langRef}>
            <button
              onClick={() => setLangOpen((p) => !p)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                isTransparent
                  ? 'text-white/80 hover:text-white hover:bg-white/10'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              <span className="text-base">{currentLang.flag}</span>
              <span className="text-xs">{currentLang.code.toUpperCase()}</span>
              <i className={`ri-arrow-down-s-line text-xs transition-transform ${langOpen ? 'rotate-180' : ''}`} />
            </button>

            {langOpen && (
              <div className="absolute right-0 top-full mt-1.5 w-44 bg-white rounded-lg border border-gray-100 py-1 z-50 overflow-hidden">
                {LANGUAGES.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => {
                      i18n.changeLanguage(lang.code);
                      setLangOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors cursor-pointer whitespace-nowrap text-left ${
                      lang.code === i18n.language
                        ? 'text-gray-900 font-medium bg-gray-50'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <span className="text-base">{lang.flag}</span>
                    <span>{lang.label}</span>
                    {lang.code === i18n.language && (
                      <i className="ri-check-line ml-auto text-xs" style={{ color: 'var(--primary)' }} />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          <Link
            to="/track"
            className="px-5 py-2.5 rounded-lg text-sm font-semibold text-white transition-all hover:opacity-90 whitespace-nowrap"
            style={{ background: 'var(--primary)' }}
          >
            {t('nav.trackNow')}
          </Link>
        </div>

        {/* Mobile toggle */}
        <button
          className={`md:hidden w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer ${
            isTransparent ? 'text-white' : 'text-gray-700'
          }`}
          onClick={() => setMobileOpen((p) => !p)}
        >
          <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`} />
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 px-6 py-4 flex flex-col gap-3">
          {NAV_LINKS.map(({ path, labelKey }) => (
            <Link
              key={path}
              to={path}
              className="py-2.5 text-sm font-medium text-gray-700 border-b border-gray-50 cursor-pointer"
              style={location.pathname === path ? { color: 'var(--primary)' } : {}}
            >
              {t(labelKey)}
            </Link>
          ))}

          {/* Mobile language selector */}
          <div className="flex items-center gap-2 pt-1 pb-0.5">
            {LANGUAGES.map((lang) => (
              <button
                key={lang.code}
                onClick={() => i18n.changeLanguage(lang.code)}
                className={`flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer border ${
                  lang.code === i18n.language
                    ? 'border-teal-600 text-teal-700 bg-teal-50'
                    : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                }`}
              >
                <span>{lang.flag}</span>
                <span>{lang.code.toUpperCase()}</span>
              </button>
            ))}
          </div>

          <Link
            to="/track"
            className="mt-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white text-center whitespace-nowrap"
            style={{ background: 'var(--primary)' }}
          >
            {t('nav.trackNow')}
          </Link>
        </div>
      )}
    </header>
  );
}
